import { Button, Divider, IconButton, Tab, Tabs, Tooltip, Typography } from "@material-ui/core";
import { createStyles, makeStyles, styled, Theme } from "@material-ui/core/styles";
import * as React from "react";
import * as ThemeConstants from "../../../Styles/Constants";
import { LetterGuessState, SocketMessageType } from "../../../Definitions/Definitions";
import { useWindowDimensions } from "../../UI/UseWindowDimensions";
import TimerIcon from '@material-ui/icons/Timer';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import FlipMove from 'react-flip-move';
import { useHistory } from "react-router";
import { Keyboard } from "../../UI/Games/Keyboard";
import { useGameContext } from "../../GameContext/GameContext";
import { OriginalGamePowerup, OriginalGamePowerupMode, OriginalGameState } from "../../GameContext/OriginalGameState";
import { LetterGrid, OverrideRowStyles } from "../../UI/Games/LetterGrid";
import { OriginalGameResultsList } from "../../UI/Games/OriginalGame/OriginalGameResultsList";
import { OriginalGamePowerupButton } from "../../UI/Games/OriginalGame/OriginalGamePowerupButton";
import { OriginalGameOpponentRow } from "../../UI/Games/OriginalGame/OriginalGameOpponentRow";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        gameBoardContainer: {
            display: "flex",
            position: "relative"
        },
        gameBoardOverlayContent: {
            transform: "translate(-50%, -50%)",
            position: "absolute",
            top: "50%",
            left: "50%",
            zIndex: 10,
            textAlign: "center"
        },
        infoRow: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "1rem"
        },
        mainContent: {
            width: "100%",
            height: "100%",
        },
        textFlex: {
            display: "flex",
            alignItems: "center",
            justifyContent: "left",
            gap: "0.5rem"
        },
        verticalFlex: {
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            gap: "1rem"
        },
        gameContent: {
            display: "flex",
            justifyContent: "center",
        },
        floatingPowerupButton: {
            position: "absolute",
            right: "1rem",
            bottom: "26%"
        },
        tab: {
            maxWidth: 75,
            minWidth: 75
        },
        mobileTab: {
            maxWidth: 75,
            minWidth: 75,
            padding: 0,
            maxHeight: 25,
            minHeight: 25
        },
        mobileScrollButtons: {
            maxHeight: 30,
            minHeight: 30
        },
        opponentScroll: {
            display: "flex",
            justifyContent: "left",
            flexDirection: "column",
            gap: "0.5rem",
            alignItems: "baseline"
        }
    })
);

export const OriginalGamePage = () => {
    const classes = useStyles();
    const history = useHistory();
    const gameContext = useGameContext();
    const { width, height } = useWindowDimensions();

    const [timerCounter, setTimerCounter] = React.useState(0);

    const {
        actualLocalPlayer, localPlayer, playerList, playerStateList, gameStarted, gameParameters, gameResults, gameStartTime, gameTimeRemaining,
        wordStates, wordsGuessed, wordIndex, guessEnabled, lastGuessInvalid,
        wordBonus, timeBonus, currentScore,
        activePowerups
    } = gameContext.gameState as OriginalGameState;

    // solution matches last guess
    const isSolved = wordStates[wordIndex].solution != "" && wordStates[wordIndex].solution == wordStates[wordIndex].guesses[wordStates[wordIndex].guesses.length - 1];

    // not solved and the solution has been received which implies failure
    const isFail = !isSolved && wordStates[wordIndex].solution != "";

    // when all words have been fully completed
    const doneGuessing = (isSolved || isFail) && wordIndex >= gameParameters.wordCount - 1;

    // when viewing previous words
    const viewingPrevious = wordIndex != wordStates.length - 1;

    const powerupButtonVisible = gameParameters.activePowerupMode != OriginalGamePowerupMode.Disabled && !doneGuessing && !viewingPrevious;
    const spectator = actualLocalPlayer?.isSpectator;

    const hasPowerup = (powerup: OriginalGamePowerup) => {
        return activePowerups.findIndex((e) => e.powerup == powerup) != -1;
    }

    const getPowerupCount = (powerup: OriginalGamePowerup) => {
        return activePowerups.filter((e) => e.powerup == powerup).length;
    }

    const keyDown = (key: string) => {
        if (spectator || isSolved || isFail || !guessEnabled || viewingPrevious) return;

        gameContext.updateGameState((prevState: OriginalGameState) => {
            const newWordStates = prevState.wordStates;
            const wordState = newWordStates[prevState.wordIndex];
            const guessIndex = wordState.guesses.length - 1;
            const guess = wordState.guesses[guessIndex];

            if (key.length == 1 && key.match(/[a-zA-Z]/)) {
                if (guess.length < gameParameters.wordLength) {
                    wordState.guesses[guessIndex] += key.toLowerCase();
                    return {
                        ...prevState,
                        wordStates: [...newWordStates]
                    }
                }
            } else if (key == "Backspace" || key == "DEL") {
                if (guess.length > 0) {
                    wordState.guesses[guessIndex] = guess.substr(0, guess.length - 1);
                    return {
                        ...prevState,
                        wordStates: [...newWordStates]
                    }
                }
            } else if (key == "Enter" || key == "ENTER") {
                if (guess.length == gameParameters.wordLength) {
                    gameContext.sendMessage(SocketMessageType.SubmitGuess, guess);
                    return {
                        ...prevState,
                        guessEnabled: false
                    }
                }
            }

            return prevState;
        });
    }

    const onBlur = (e: React.FocusEvent<HTMLDivElement>) => {
        setTimeout(() => e.target.focus(), 100);
    }

    const updateWordIndex = (index: number) => {
        gameContext.updateGameState((prevState: OriginalGameState) => {
            return {
                ...prevState,
                wordIndex: index
            }
        });
    }

    const nextWord = () => {
        if (spectator) return;

        gameContext.updateGameState((prevState: OriginalGameState) => {
            return {
                ...prevState,
                wordStates: [...prevState.wordStates, { guesses: [""], matches: [], solution: "", invalidLetters: [], positionLetters: [], matchLetters: [] }],
                wordIndex: prevState.wordIndex + 1,
                currentScore: prevState.currentScore + prevState.wordBonus,
                wordBonus: 0
            }
        });
    }

    const getOtherPlayerCount = () => {
        return playerList.filter(e => (!e.isSpectator || e.id in playerStateList) && localPlayer && localPlayer.id != e.id).length;
    }

    const getHintWordArray = () => {
        let letters: string[] = Array(gameParameters.wordLength).fill(" ");

        activePowerups.forEach((p) => {
            if (p.powerup != OriginalGamePowerup.Hint) return;
            if (!("letter" in p.data)) return; // data will be empty if the hint is invalid for any reason
            if (p.data.wordIndex != wordIndex) return; // ignore hints for previous words
            letters[p.data.index] = String.fromCharCode(p.data.letter);
        });

        const hintString = letters.join("");
        return Array(gameParameters.guessCount).fill(hintString);
    }

    const getHintMatchArray = () => {
        let matches: LetterGuessState[] = Array(gameParameters.wordLength).fill(LetterGuessState.Invalid);

        activePowerups.forEach((p) => {
            if (p.powerup != OriginalGamePowerup.Hint) return;
            if (!("letter" in p.data)) return; // data will be empty if the hint failed for any reason
            if (p.data.wordIndex != wordIndex) return; // ignore hints for previous words
            matches[p.data.index] = p.data.match;
        });

        return Array(gameParameters.guessCount).fill(matches);
    }

    const getBoardOverrideRowStyles = () => {
        let styles: OverrideRowStyles[] = [];

        // apply hidden guesses first so any color flips will be overridden
        activePowerups.forEach((p) => {
            if (p.powerup != OriginalGamePowerup.HideGuess) return;
            if (!("affectedRow" in p.data)) return; // data will be empty if the powerup failed for any reason
            if (p.data.wordIndex != wordIndex) return; // ignore hints for previous words
            styles.push({
                rowIndex: p.data.affectedRow,
                invalidColor: ThemeConstants.PALETTE_BLACK,
                positionColor: ThemeConstants.PALETTE_BLACK,
                matchColor: ThemeConstants.PALETTE_BLACK,
                invalidOpacity: 1.0,
                positionOpacity: 1.0,
                matchOpacity: 1.0,
                hideText: true
            });
        });

        // apply color flips
        activePowerups.forEach((p) => {
            if (p.powerup != OriginalGamePowerup.ColorFlip) return;
            if (!("affectedRow" in p.data)) return; // data will be empty if the powerup failed for any reason
            if (p.data.wordIndex != wordIndex) return; // ignore hints for previous words
            styles.push({
                rowIndex: p.data.affectedRow,
                invalidColor: ThemeConstants.GAME_BOX_DEFAULT_COLOR,
                positionColor: ThemeConstants.GAME_BOX_MATCH_COLOR,
                matchColor: ThemeConstants.GAME_BOX_POSITION_COLOR
            });
        });

        // turn the guessing row red if the last guess was invalid
        if (lastGuessInvalid) {
            styles.push({
                rowIndex: wordStates[wordIndex].guesses.length - 1,
                invalidColor: ThemeConstants.PALETTE_RED
            });
        }

        return styles;
    }

    const renderBoardOverlayButton = () => {
        if ((isSolved || isFail) && !viewingPrevious) {
            if (wordIndex >= gameParameters.wordCount - 1 && actualLocalPlayer?.id == localPlayer?.id)
                return <Typography variant="h4" className={classes.gameBoardOverlayContent} style={{ zIndex: 15, color: ThemeConstants.PALETTE_GREEN }}><b>You're done!</b></Typography>;
            else if (!spectator) {
                return (
                    <Button className={classes.gameBoardOverlayContent} style={{ zIndex: 15 }} onClick={nextWord} color="primary" variant="contained">
                        Next word
                    </Button>
                );
            }
        }
    }

    const formatRemainingTime = () => {
        if (gameParameters.minutes == 0)
            return "*:*";

        const minutes = Math.trunc(gameTimeRemaining / 60);
        const seconds = (gameTimeRemaining % 60).toString();

        return `${minutes}:${seconds.length == 1 ? "0" + seconds : seconds}`
    }

    const stopSpectating = () => {
        if (!actualLocalPlayer) return;

        // by spectating our original local player, we are effectively 'exiting' spectator mode
        gameContext.sendMessage(SocketMessageType.SpectatePlayer, actualLocalPlayer.id);
    }

    React.useEffect(() => {
        if (doneGuessing && actualLocalPlayer && !actualLocalPlayer.isSpectator) {
            gameContext.sendMessage(SocketMessageType.JoinSpectators, "");
        }
    }, [doneGuessing]);

    React.useEffect(() => {
        if (gameStarted)
            document.getElementById("gameboard")?.focus();
    }, [gameStarted]);

    React.useEffect(() => {
        if (gameStarted && gameTimeRemaining > 0 && gameParameters.minutes > 0) {
            const timer = setTimeout(() => {
                const utcTimestampMS = Date.parse((new Date()).toUTCString());
                const gameStartOffset = Math.round((utcTimestampMS - gameStartTime) / 1000);
                const newTime = 60 * gameParameters.minutes - gameStartOffset;

                setTimerCounter(timerCounter + 1);

                gameContext.updateGameState((prevState: OriginalGameState) => {
                    const newBonus = gameParameters.timeBonusEnabled ? newTime * prevState.wordsGuessed * 2 : 0;
                    return {
                        ...prevState,
                        gameTimeRemaining: newTime,
                        timeBonus: doneGuessing ? prevState.timeBonus : newBonus
                    }
                });
            }, 250);

            return () => clearTimeout(timer);
        }
    }, [gameStarted, timerCounter]);

    return (
        <div className={classes.mainContent} style={{ marginTop: width < 600 ? "1rem" : "2rem" }}>
            {gameResults ?
                <OriginalGameResultsList />
                :
                <div className={classes.gameContent} style={{ gap: width < 600 ? "0.5rem" : "2rem" }}>
                    {width >= 900 &&
                        <div className={classes.verticalFlex} style={{ alignItems: "baseline" }}>
                            <div style={{ minWidth: 200 }}>
                                <Typography variant="h5">Time: <b>{formatRemainingTime()}</b></Typography>
                                <Typography variant="h5">Words: <b>{`${doneGuessing ? wordStates.length : wordStates.length - 1}/${gameParameters.wordCount}`}</b></Typography>
                                <Typography variant="h5">Score: <b>{currentScore}</b></Typography>
                                <div className={classes.textFlex}>
                                    <img src="favicon.ico" width={20} />
                                    <Typography variant="h5">Bonus: <b>{wordBonus}</b></Typography>
                                    {getPowerupCount(OriginalGamePowerup.ScoreMultiplier) > 0 &&
                                        <Typography variant="h6"><b>(X{getPowerupCount(OriginalGamePowerup.ScoreMultiplier) * 2})</b></Typography>
                                    }
                                </div>
                                {(gameParameters.minutes > 0 && gameParameters.timeBonusEnabled) &&
                                    <div className={classes.textFlex}>
                                        <TimerIcon fontSize={"small"} />
                                        <Typography variant="h5">Bonus: <b>{timeBonus}</b></Typography>
                                    </div>
                                }
                                {(width >= 900 && powerupButtonVisible) &&
                                    <OriginalGamePowerupButton />
                                }
                            </div>
                        </div>
                    }
                    <div className={classes.verticalFlex}>
                        {width < 900 &&
                            <div className={classes.infoRow}>
                                <Typography variant="h5" style={{ minWidth: 50 }}><b>{formatRemainingTime()}</b></Typography>
                                <Typography variant="h5" style={{ minWidth: 50 }}><b>{`${doneGuessing ? wordStates.length : wordStates.length - 1}/${gameParameters.wordCount}`}</b></Typography>
                                <Typography variant="h5" style={{ minWidth: 50 }}><b>{currentScore + wordBonus + timeBonus}</b></Typography>
                            </div>
                        }
                        <div className={classes.gameBoardContainer}>
                            <div
                                id="gameboard"
                                onKeyDown={(e) => keyDown(e.key)}
                                onBlur={onBlur}
                                tabIndex={0}
                                style={{ opacity: (isSolved || isFail) ? "0.2" : "1.0", outline: "none" }}
                            >
                                <LetterGrid
                                    rows={gameParameters.guessCount}
                                    cols={gameParameters.wordLength}
                                    words={wordStates[wordIndex].guesses}
                                    matches={wordStates[wordIndex].matches}
                                    animate={wordStates[wordIndex].guesses.length > 1 && !viewingPrevious && !lastGuessInvalid /* dont animate when the board is changing because it flashes */}
                                    positionColor={ThemeConstants.GAME_BOX_POSITION_COLOR}
                                    matchColor={ThemeConstants.GAME_BOX_MATCH_COLOR}
                                    overrideRowStyles={getBoardOverrideRowStyles()}
                                    size="lg"
                                />
                            </div>
                            {renderBoardOverlayButton()}
                            <div className={classes.gameBoardOverlayContent} style={{ pointerEvents: "none" }}>
                                <LetterGrid
                                    rows={gameParameters.guessCount}
                                    cols={gameParameters.wordLength}
                                    words={getHintWordArray()}
                                    matches={getHintMatchArray()}
                                    positionColor={ThemeConstants.GAME_BOX_POSITION_COLOR}
                                    matchColor={ThemeConstants.GAME_BOX_MATCH_COLOR}
                                    invalidOpacity={0}
                                    positionOpacity={0.16}
                                    matchOpacity={0.16}
                                    size="lg"
                                />
                            </div>
                        </div>
                        {(spectator && localPlayer?.id != actualLocalPlayer?.id) &&
                            <div className={classes.textFlex} style={{ marginTop: "-0.75rem", gap: "0.2rem" }}>
                                <Typography variant="h6" style={{ fontSize: width < 600 ? 12 : "" }} color="textSecondary">Spectating {localPlayer?.name.substr(0, width < 600 ? 10 : 12)}</Typography>
                                {(actualLocalPlayer?.startedAsPlayer) &&
                                    <Tooltip title={<Typography variant="body1">Stop spectating this player</Typography>}>
                                        <IconButton onClick={stopSpectating} style={{ padding: "5px" }}>
                                            <HighlightOffIcon color="secondary" style={{ fontSize: width < 600 ? 18 : 24 }} />
                                        </IconButton>
                                    </Tooltip>
                                }
                            </div>
                        }
                        <Tabs
                            variant="scrollable"
                            style={{ maxWidth: "400px", maxHeight: "35vh", marginTop: "-0.75rem" }}
                            value={wordIndex}
                            orientation={width < 600 ? "vertical" : "horizontal"}
                            scrollButtons={width < 600 ? "on" : "auto"}
                            onChange={(e, v) => updateWordIndex(v as number)}
                            classes={width < 600 ? { scrollButtons: classes.mobileScrollButtons } : {}}
                        >
                            {
                                wordStates.map((s, i) => {
                                    return <Tab key={i} classes={{ root: width < 600 ? classes.mobileTab : classes.tab }} label={s.solution == "" ? "???" : s.solution} value={i}/>
                                })
                            }
                        </Tabs>
                    </div>
                    {getOtherPlayerCount() > 0 &&
                        <div style={{ display: "flex", flexDirection: "column", gap: width < 600 ? "0.5rem" : "1rem" }}>
                            <Typography variant="h6" style={{ marginBottom: "-0.5rem", marginTop: "2rem" }}>Recent Activity</Typography>
                            <Divider />
                            <div id="opponentScroll" style={{ maxHeight: width < 600 ? "50vh" : "40vh", overflowY: "auto", overflowX: "hidden", width: "min-content" }}>
                                <FlipMove className={classes.opponentScroll} style={{ paddingRight: width < 600 ? 0 : "20px" }}>
                                    {
                                        playerList.map((elem) => {
                                            if (!localPlayer || localPlayer.id == elem.id) return;
                                            if (!(elem.id in playerStateList)) return;

                                            return (
                                                <div key={elem.id}>
                                                    <OriginalGameOpponentRow player={elem} />
                                                </div>
                                            );
                                        })
                                    }
                                </FlipMove>
                            </div>
                        </div>
                    }
                </div>
            }
            {!gameResults &&
                <Keyboard
                    // hide colors when powerups are active
                    invalidLetters={wordStates[wordIndex].invalidLetters}
                    positionLetters={
                        (hasPowerup(OriginalGamePowerup.ColorFlip) ||
                         hasPowerup(OriginalGamePowerup.HideGuess)) ? [] :
                        wordStates[wordIndex].positionLetters
                    }
                    matchLetters={
                        (hasPowerup(OriginalGamePowerup.ColorFlip) ||
                         hasPowerup(OriginalGamePowerup.HideGuess)) ? [] :
                        wordStates[wordIndex].matchLetters
                    }
                    onKeyPressed={keyDown}
                    positionColor={ThemeConstants.GAME_BOX_POSITION_COLOR}
                    matchColor={ThemeConstants.GAME_BOX_MATCH_COLOR}
                />
            }
            {(!gameResults && width < 900) &&
                <div className={classes.floatingPowerupButton}>
                    {powerupButtonVisible &&
                        <OriginalGamePowerupButton />
                    }
                </div>
            }
        </div>
    );
}